/**
 * 后台「场景预览」Tab：批量发起 + 历史任务列表。
 *
 * - 上：三入口（按 SQL / 场景名 / 集合树节点）批量提交 SCENE_PREVIEW Job
 * - 下：分页列出 ``task_type_name='SCENE_PREVIEW'`` 的 Job 历史
 *   （走 ``/admin/scene-preview/jobs``，绕开 ``/job/list`` 的 enable_flag=1 限制）
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
  Alert, App, Button, Card, Input, Progress, Select, Space, Table, Tabs, Tag,
  Tooltip, TreeSelect, Typography,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import {
  EyeInvisibleOutlined, FileOutlined, FolderOutlined, LockOutlined,
  PlayCircleOutlined, ReloadOutlined,
} from '@ant-design/icons';
import type { Key } from 'react';

import {
  getScenePreviewJobs, getSceneTree, getStatusList, JobItem,
  ScenePreviewDispatchVo, SceneTreeNode, StatusOption,
  triggerScenePreviewByNames, triggerScenePreviewBySql,
  triggerScenePreviewByTreeNodes,
} from '@apis/simulation';
import JobStatusTag from '@/components/StatusTag/JobStatusTag';
import { completionPercent } from '@/common/progress';

const { Title, Text } = Typography;
const { TextArea } = Input;


// ============================================================================
// 共用：发起结果展示
// ============================================================================

function PreviewResultPanel({ result }: { result: ScenePreviewDispatchVo | null }) {
  if (!result) return null;
  return (
    <Alert
      type="success"
      showIcon
      style={{ marginTop: 12 }}
      message={
        <Space size={16} wrap>
          <span>本平台 jobId：<code>{result.jobId}</code></span>
          <span>调度平台 dispatchJobId：<code>{result.dispatchJobId || '-'}</code></span>
          <span>发起场景数：<Tag color="green">{result.total}</Tag></span>
          {!!result.skippedNoSceneInfo && (
            <span>
              跳过（缺 scene_info_address）：
              <Tag color="orange">{result.skippedNoSceneInfo}</Tag>
            </span>
          )}
          {!!result.skippedNotFound && (
            <span>跳过（未匹配）：<Tag color="orange">{result.skippedNotFound}</Tag></span>
          )}
        </Space>
      }
    />
  );
}


// ============================================================================
// 三个发起入口
// ============================================================================

function PreviewBySqlPanel({ onSubmitted }: { onSubmitted: () => void }) {
  const { message } = App.useApp();
  const [sql, setSql] = useState('');
  const [jobName, setJobName] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState<ScenePreviewDispatchVo | null>(null);

  const handleSubmit = async () => {
    const trimmed = sql.trim();
    if (!trimmed) {
      message.error('请输入 SQL');
      return;
    }
    setSubmitting(true);
    try {
      const r = await triggerScenePreviewBySql(trimmed, jobName.trim() || undefined);
      setResult(r);
      message.success(`已发起预览 Job=${r.jobId}（共 ${r.total} 条场景）`);
      onSubmitted();
    } catch (e: any) {
      message.error(e?.response?.data?.message || '发起失败');
      console.error(e);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Alert
        type="info"
        showIcon
        message="按 SQL"
        description={
          <span>
            只读 <code>SELECT</code> / <code>WITH</code>，结果必须返回 <code>id</code> 列
            （即 <code>scene_inspection.id</code>）。多语句 / 写操作会被拒。
          </span>
        }
        style={{ marginBottom: 12 }}
      />
      <Input
        placeholder="可选：Job 名称前缀（默认 scene-preview-by-sql）"
        value={jobName}
        onChange={(e) => setJobName(e.target.value)}
        style={{ marginBottom: 8 }}
      />
      <TextArea
        value={sql}
        onChange={(e) => setSql(e.target.value)}
        autoSize={{ minRows: 4, maxRows: 12 }}
        placeholder="SELECT id FROM scene_inspection WHERE function_tag = 'PL' LIMIT 100"
        style={{ fontFamily: 'monospace' }}
      />
      <Space style={{ marginTop: 12 }}>
        <Button
          type="primary"
          icon={<PlayCircleOutlined />}
          loading={submitting}
          onClick={handleSubmit}
        >
          发起预览
        </Button>
        <Button onClick={() => { setSql(''); setResult(null); }}>清空</Button>
      </Space>
      <PreviewResultPanel result={result} />
    </>
  );
}

function PreviewByNamesPanel({ onSubmitted }: { onSubmitted: () => void }) {
  const { message } = App.useApp();
  const [text, setText] = useState('');
  const [jobName, setJobName] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState<ScenePreviewDispatchVo | null>(null);

  const parsedNames = useMemo(() => {
    const seen = new Set<string>();
    const out: string[] = [];
    for (const tok of text.replace(/,/g, '\n').split(/\s+/)) {
      const t = tok.trim();
      if (t && !seen.has(t)) {
        seen.add(t);
        out.push(t);
      }
    }
    return out;
  }, [text]);

  const handleSubmit = async () => {
    if (!parsedNames.length) {
      message.error('请输入至少一个场景名');
      return;
    }
    setSubmitting(true);
    try {
      const r = await triggerScenePreviewByNames(
        parsedNames, jobName.trim() || undefined,
      );
      setResult(r);
      message.success(`已发起预览 Job=${r.jobId}（共 ${r.total} 条场景）`);
      onSubmitted();
    } catch (e: any) {
      message.error(e?.response?.data?.message || '发起失败');
      console.error(e);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Alert
        type="info"
        showIcon
        message="按场景名"
        description={
          <span>
            支持多个场景名（逗号 / 换行 / 空白分隔，自动去重）；
            找不到对应 <code>scene_inspection</code> 行的会计入 <code>skippedNotFound</code>。
          </span>
        }
        style={{ marginBottom: 12 }}
      />
      <Input
        placeholder="可选：Job 名称前缀（默认 scene-preview-by-names）"
        value={jobName}
        onChange={(e) => setJobName(e.target.value)}
        style={{ marginBottom: 8 }}
      />
      <TextArea
        value={text}
        onChange={(e) => setText(e.target.value)}
        autoSize={{ minRows: 4, maxRows: 12 }}
        placeholder={'LPL0000290333\nLPL0000290334\nLPL0000290335'}
        style={{ fontFamily: 'monospace' }}
      />
      <div style={{ marginTop: 6 }}>
        <Text type="secondary">
          解析到 <Tag color="blue">{parsedNames.length}</Tag> 个场景名（去重后）
        </Text>
      </div>
      <Space style={{ marginTop: 12 }}>
        <Button
          type="primary"
          icon={<PlayCircleOutlined />}
          loading={submitting}
          onClick={handleSubmit}
        >
          发起预览
        </Button>
        <Button onClick={() => { setText(''); setResult(null); }}>清空</Button>
      </Space>
      <PreviewResultPanel result={result} />
    </>
  );
}

function PreviewByTreeNodesPanel(
  { treeData, onSubmitted }: { treeData: SceneTreeNode[]; onSubmitted: () => void },
) {
  const { message } = App.useApp();
  const [selectedKeys, setSelectedKeys] = useState<Key[]>([]);
  const [jobName, setJobName] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState<ScenePreviewDispatchVo | null>(null);

  const treeSelectData = useMemo(() => {
    const convert = (nodes: SceneTreeNode[]): any[] =>
      nodes.map((n) => ({
        value: n.id,
        title: (
          <span>
            {n.nodeFlag === 1
              ? <FileOutlined style={{ marginRight: 4, color: '#999' }} />
              : <FolderOutlined style={{ marginRight: 4, color: '#faad14' }} />}
            {n.name}
            {n.isLocked && (
              <LockOutlined style={{ marginLeft: 6, color: '#fa8c16' }} />
            )}
            {n.isHidden && (
              <EyeInvisibleOutlined style={{ marginLeft: 6, color: '#999' }} />
            )}
          </span>
        ),
        children: n.children?.length ? convert(n.children) : undefined,
      }));
    return convert(treeData);
  }, [treeData]);

  const handleSubmit = async () => {
    const ids = selectedKeys.map((k) => Number(k)).filter(Boolean);
    if (!ids.length) {
      message.error('请至少选择一个节点');
      return;
    }
    setSubmitting(true);
    try {
      const r = await triggerScenePreviewByTreeNodes(
        ids, jobName.trim() || undefined,
      );
      setResult(r);
      message.success(`已发起预览 Job=${r.jobId}（共 ${r.total} 条场景）`);
      onSubmitted();
    } catch (e: any) {
      message.error(e?.response?.data?.message || '发起失败');
      console.error(e);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Alert
        type="info"
        showIcon
        message="按场景集合 / 目录"
        description={
          <span>
            选中目录会递归取所有子叶下的场景；可多选不同集合。
            隐藏 / 锁定节点在此处仍可见可选（admin 视角包含全部）。
          </span>
        }
        style={{ marginBottom: 12 }}
      />
      <Input
        placeholder="可选：Job 名称前缀（默认 scene-preview-by-tree-nodes）"
        value={jobName}
        onChange={(e) => setJobName(e.target.value)}
        style={{ marginBottom: 8 }}
      />
      <TreeSelect
        treeData={treeSelectData}
        value={selectedKeys as any}
        onChange={(keys) => setSelectedKeys(Array.isArray(keys) ? keys : [keys])}
        multiple
        treeCheckable
        showCheckedStrategy="SHOW_PARENT"
        placeholder="选择 1 个或多个节点"
        style={{ width: '100%' }}
        treeDefaultExpandAll
        allowClear
      />
      <Space style={{ marginTop: 12 }}>
        <Button
          type="primary"
          icon={<PlayCircleOutlined />}
          loading={submitting}
          onClick={handleSubmit}
        >
          发起预览
        </Button>
        <Button onClick={() => { setSelectedKeys([]); setResult(null); }}>
          清空
        </Button>
      </Space>
      <PreviewResultPanel result={result} />
    </>
  );
}


// ============================================================================
// 预览任务列表（task_type_name=SCENE_PREVIEW）
// ============================================================================

/**
 * 把 ISO 时间格式化为「YYYY-MM-DD HH:mm:ss」+ 相对时间提示。
 * 后端有可能回 "2026-05-15T17:53:32" 也可能回 "2026-05-15 17:53:32"，统一处理。
 */
function formatTime(v: string | null | undefined): { text: string; relative: string } {
  if (!v) return { text: '-', relative: '' };
  const normalized = v.replace('T', ' ').slice(0, 19);
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return { text: normalized, relative: '' };
  const diffSec = Math.floor((Date.now() - d.getTime()) / 1000);
  let relative: string;
  if (diffSec < 60) relative = `${diffSec} 秒前`;
  else if (diffSec < 3600) relative = `${Math.floor(diffSec / 60)} 分钟前`;
  else if (diffSec < 86400) relative = `${Math.floor(diffSec / 3600)} 小时前`;
  else relative = `${Math.floor(diffSec / 86400)} 天前`;
  return { text: normalized, relative };
}

function PreviewJobListPanel({ refreshKey }: { refreshKey: number }) {
  const { message } = App.useApp();
  const navigate = useNavigate();
  const [rows, setRows] = useState<JobItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [current, setCurrent] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [total, setTotal] = useState(0);
  const [jobName, setJobName] = useState('');
  const [statusFilter, setStatusFilter] = useState<number | undefined>(undefined);
  const [statusOptions, setStatusOptions] = useState<StatusOption[]>([]);

  // 状态字典只拉一次（与 JobList 共用同一份枚举接口）
  useEffect(() => {
    getStatusList().then(setStatusOptions).catch(() => { /* 静默；下拉退化为只显示已知态 */ });
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await getScenePreviewJobs({
        current, pageSize,
        jobName: jobName.trim() || undefined,
        status: statusFilter,
      });
      setRows(r.tasks);
      setTotal(r.page?.totalElements ?? r.total ?? 0);
    } catch (e) {
      message.error('加载预览任务列表失败');
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [current, pageSize, jobName, statusFilter, message]);

  useEffect(() => {
    load();
  }, [load, refreshKey]);

  const columns: ColumnsType<JobItem> = useMemo(
    () => [
      {
        title: 'Job ID', dataIndex: 'jobId', width: 80, fixed: 'left',
        render: (v: number) => (
          <Typography.Text
            style={{ fontFamily: 'ui-monospace, Menlo, monospace' }}
          >
            {v}
          </Typography.Text>
        ),
      },
      {
        title: '任务名称', dataIndex: 'jobName', width: 260,
        ellipsis: { showTitle: false },
        render: (v: string, r) => (
          <Tooltip title={`${v || '-'}（点击查看任务详情）`} placement="topLeft">
            <Link
              to={`/job-tasks/${r.jobId}`}
              style={{ maxWidth: 240, display: 'inline-block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', verticalAlign: 'bottom' }}
            >
              {v || '-'}
            </Link>
          </Tooltip>
        ),
      },
      {
        title: '执行进度', width: 180,
        render: (_: any, r) => {
          const total = r.totalTaskNum ?? 0;
          const success = r.successTaskNum ?? 0;
          const fail = r.failTaskNum ?? 0;
          const finished = success + fail;
          const percent = completionPercent(finished, total);
          // 失败行视为 "exception"（红色）；全成功 "success"；其他正常进度
          const status: 'success' | 'exception' | 'normal' =
            fail > 0 ? 'exception' : (total > 0 && success === total ? 'success' : 'normal');
          // 详细计数挂在 Tooltip 里，进度条本身保持简洁
          const tip = (
            <Space direction="vertical" size={2}>
              <span>共 {total} 个场景</span>
              <span style={{ color: '#73d13d' }}>成功 {success}</span>
              <span style={{ color: '#ff7875' }}>失败 {fail}</span>
              <span style={{ color: '#bfbfbf' }}>已完成 {finished}/{total}（{percent}%）</span>
            </Space>
          );
          return (
            <Tooltip title={tip} placement="top">
              <div style={{ minWidth: 160, cursor: 'help' }}>
                <Progress
                  percent={percent}
                  size="small"
                  status={status}
                  showInfo={false}
                />
              </div>
            </Tooltip>
          );
        },
      },
      {
        title: '调度 ID', dataIndex: 'dispatchJobId', width: 220,
        ellipsis: { showTitle: false },
        render: (v: string) => v ? (
          <Tooltip title={v} placement="topLeft">
            <Typography.Text
              copyable={{ text: v, tooltips: ['复制', '已复制'] }}
              style={{ fontFamily: 'ui-monospace, Menlo, monospace', fontSize: 12 }}
              ellipsis
            >
              {v}
            </Typography.Text>
          </Tooltip>
        ) : <Typography.Text type="secondary">-</Typography.Text>,
      },
      {
        title: '创建人', dataIndex: 'creator', width: 110, ellipsis: true,
        render: (v: string | null) => v || <Typography.Text type="secondary">-</Typography.Text>,
      },
      {
        title: '创建时间', dataIndex: 'createTime', width: 170,
        render: (v: string) => {
          const { text, relative } = formatTime(v);
          return (
            <Tooltip title={relative ? `${text}（${relative}）` : text}>
              <Typography.Text style={{ fontSize: 12 }}>{text}</Typography.Text>
            </Tooltip>
          );
        },
      },
      {
        title: '状态', dataIndex: 'status', width: 110, align: 'center',
        fixed: 'right',
        render: (_: any, r) => (
          <JobStatusTag status={r.status} statusName={r.statusName} />
        ),
      },
    ],
    [navigate],
  );

  return (
    <Card
      title="预览任务列表"
      extra={
        <Space wrap>
          <Select<number>
            allowClear
            placeholder="按状态过滤"
            value={statusFilter}
            onChange={(v) => { setStatusFilter(v); setCurrent(1); }}
            style={{ width: 140 }}
            options={statusOptions.map(o => ({
              value: typeof o.value === 'string' ? Number(o.value) : o.value,
              label: o.label,
            }))}
          />
          <Input.Search
            placeholder="按任务名搜索"
            value={jobName}
            onChange={(e) => setJobName(e.target.value)}
            onSearch={() => { setCurrent(1); load(); }}
            allowClear
            style={{ width: 220 }}
          />
          <Button icon={<ReloadOutlined />} onClick={load} loading={loading}>
            刷新
          </Button>
        </Space>
      }
    >
      <Table
        rowKey="jobId"
        columns={columns}
        dataSource={rows}
        loading={loading}
        size="small"
        scroll={{ x: 1130 }}
        pagination={{
          current, pageSize, total,
          showSizeChanger: true,
          pageSizeOptions: [10, 20, 50, 100],
          onChange: (p, ps) => { setCurrent(p); setPageSize(ps); },
          showTotal: (t) => `共 ${t} 条`,
        }}
        locale={{
          emptyText: loading ? '加载中…' : '暂无场景预览任务，去上方任一入口发起一次试试',
        }}
      />
    </Card>
  );
}


// ============================================================================
// Tab 入口
// ============================================================================

export default function ScenePreviewTab() {
  const { message } = App.useApp();
  const [treeData, setTreeData] = useState<SceneTreeNode[]>([]);
  const [refreshKey, setRefreshKey] = useState(0);

  const loadTree = useCallback(async () => {
    try {
      // admin 视角拿全（含隐藏），便于按目录批量预览
      const t = await getSceneTree(true);
      setTreeData(t);
    } catch (e) {
      message.error('加载场景树失败');
      console.error(e);
    }
  }, [message]);

  useEffect(() => { loadTree(); }, [loadTree]);

  const triggerListReload = useCallback(() => setRefreshKey((k) => k + 1), []);

  return (
    <Space direction="vertical" size={16} style={{ width: '100%' }}>
      <div>
        <Title level={5} style={{ marginTop: 0 }}>批量发起场景预览</Title>
        <Text type="secondary">
          执行 <code>scene_preview.py --upload</code>。三种入口殊途同归，最终落 SCENE_PREVIEW
          类型的 Job 与 Task；进度可在下方「预览任务列表」查看。
        </Text>
      </div>

      <Card>
        <Tabs
          items={[
            {
              key: 'sql', label: '按 SQL',
              children: <PreviewBySqlPanel onSubmitted={triggerListReload} />,
            },
            {
              key: 'names', label: '按场景名',
              children: <PreviewByNamesPanel onSubmitted={triggerListReload} />,
            },
            {
              key: 'tree-nodes', label: '按集合 / 目录',
              children: (
                <PreviewByTreeNodesPanel
                  treeData={treeData}
                  onSubmitted={triggerListReload}
                />
              ),
            },
          ]}
        />
      </Card>

      <PreviewJobListPanel refreshKey={refreshKey} />
    </Space>
  );
}
